import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faBars,
  faCalendarPlus,
  faEnvelope,
  faHouse,
  faLink,
  faMoonStars,
  faRightFromBracket,
  faShareFromSquare,
  faSunBright,
  faUser,
} from '@fortawesome/pro-duotone-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';

import { ContextMenu } from '../../../../../shared/components/ui/context-menu/context-menu';
import { ContextMenuTriggerDirective } from '../../../../../shared/components/ui/context-menu/context-menu-trigger.directive';
import { SquareIconBtn } from '../../../../../shared/components/ui/buttons/square-icon-btn/square-icon-btn';
import { TooltipDirective } from '../../../../../shared/components/ui/tooltip/tooltip.directive';
import { AppStore } from '../../../../../state/app.repository';
import { UserSettingsStore } from '../../../../../state/user-settings.repository';
import { UserStore } from '../../../../../state/user.repository';
import { RoomFiles } from '../../../../../features/files';
import { OdinConnectionStatus, OdinService } from '../../../../services/odin.service';
import { RoomActionsService } from '../../../../services/room-actions.service';
import { MeetingTime } from '../../../meeting-time/meeting-time';

/**
 * Room info in the header: a frosted pill with the room name and a status
 * dot, then share, color mode and leave. In the conference view it adds the
 * number of users in the room, the meeting timing (connected for, scheduled
 * window, overrun) and the room's files; in the preparation area (before
 * joining) it offers "Back to Home" instead of leave. On narrow screens name,
 * status, user count and timing move into a menu.
 */
@Component({
  selector: 'andor-room-info',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    FaDuotoneIconComponent,
    TranslatePipe,
    ContextMenu,
    ContextMenuTriggerDirective,
    SquareIconBtn,
    TooltipDirective,
    RoomFiles,
    MeetingTime,
  ],
  templateUrl: './room-info.html',
  styleUrl: './room-info.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomInfo {
  private readonly _userSettings = inject(UserSettingsStore);
  private readonly _odin = inject(OdinService);
  private readonly _appStore = inject(AppStore);
  private readonly _users = inject(UserStore);
  protected readonly actions = inject(RoomActionsService);

  protected readonly faBars = faBars;
  protected readonly faShareFromSquare = faShareFromSquare;
  protected readonly faLink = faLink;
  protected readonly faEnvelope = faEnvelope;
  protected readonly faCalendarPlus = faCalendarPlus;
  protected readonly faMoonStars = faMoonStars;
  protected readonly faSunBright = faSunBright;
  protected readonly faRightFromBracket = faRightFromBracket;
  protected readonly faHouse = faHouse;
  protected readonly faUser = faUser;

  protected readonly roomName = toSignal(this._userSettings.roomName$, { initialValue: '' });
  protected readonly colorMode = toSignal(this._userSettings.colorMode$, {
    initialValue: this._userSettings.colorMode,
  });

  /** Joined: meeting timing and files only make sense inside the conference view. */
  protected readonly inConference = toSignal(this._appStore.isConnectedView$, {
    initialValue: this._appStore.value.isConnectedView,
  });
  /** Everyone the users list shows: all breakout rooms, the own user included. */
  protected readonly userCount = toSignal(this._users.userCount$, { initialValue: 0 });
  private readonly _section = toSignal(this._appStore.activeSection$, {
    initialValue: this._appStore.value.activeSection,
  });
  /** Before joining, the way out is back to the room login instead of leaving. */
  protected readonly showBackHome = computed(
    () =>
      this._section() === 'preparation-area' &&
      this._odin.status() !== OdinConnectionStatus.connected
  );

  protected readonly statusClass = computed(() => {
    switch (this._odin.status()) {
      case OdinConnectionStatus.connected:
        return 'ok';
      case OdinConnectionStatus.connecting:
      case OdinConnectionStatus.reconnecting:
        return 'pending';
      default:
        return 'down';
    }
  });

  protected readonly statusLabel = computed(() => {
    switch (this._odin.status()) {
      case OdinConnectionStatus.connected:
        return _('Connected');
      case OdinConnectionStatus.connecting:
        return _('Connecting…');
      case OdinConnectionStatus.reconnecting:
        return _('Reconnecting…');
      case OdinConnectionStatus.disconnecting:
        return _('Disconnecting…');
      default:
        return _('Disconnected');
    }
  });

  protected toggleColorMode() {
    this._userSettings.toggleColorMode();
  }
}
